import { Button, DialogOverlay, DialogOverlayActions, Separator, TextArea, useOverlay, Paragraph } from '@fs/zion-ui'
import React, { useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../hooks/useNotifications'
import { copyPersonToOwners } from '../../../services/personService'
import { isAlphaNumericCisId } from '../../../util/EntityUtil'
import { getIdElement } from '../../../util/StringUtil'
import { PersonContext } from '../PersonContext'

export default function CreateCopyButton({ refetchPersonCopies }) {
  const { t } = useTranslation()
  const { error, info, ok } = useNotifications()
  const { currentPerson } = useContext(PersonContext)
  const overlay = useOverlay()
  const [cisIds, setCisIds] = useState('')
  const focusElementRef = useRef()

  const submitOnClick = async () => {
    overlay.close()
    try {
      const cisIdsList = cisIds
        .trim()
        .split(/,\n|;\n|\n|,|;|\s+/g)
        .filter((id) => id !== '')
      const response = await copyPersonToOwners(currentPerson.id, cisIdsList, null)
      if (response.failedMessage) {
        error(response.failedMessage)
      }
      if (response.notifications.length > 0) {
        info(response.notifications[0].msg)
      }
      if (response.successMessage) {
        if (!response.failedMessage && response.notifications.length === 0) {
          ok(t('success', 'Success'))
          setCisIds('')
        } else {
          ok(response.successMessage)
        }
        refetchPersonCopies()
      }
    } catch (err) {
      error(
        t('createPersonCopies.error', {
          defaultValue: `Error creating person copies: ${err.message || ''}`,
          message: err.message || '',
        }),
        err
      )
    }
  }

  const getValidMessage = () => {
    if (!cisIds) {
      return null
    }
    const cisIdsList = cisIds
      .trim()
      .split(/,\n|;\n|\n|,|;|\s+/g)
      .filter((id) => id !== '')
    if (cisIdsList.length === 0) {
      return null
    }
    if (cisIdsList.some((cisId) => !isAlphaNumericCisId(getIdElement(cisId)))) {
      return null
    }
    return t('valid', 'Valid')
  }

  const getErrorMessage = () => {
    if (!cisIds) {
      return null
    }
    const cisIdsList = cisIds
      .trim()
      .split(/,\n|;\n|\n|,|;|\s+/g)
      .filter((id) => id !== '')
    if (cisIdsList.length === 0) {
      return t('invalidInput', 'Invalid input: {input}', {
        input: cisIds,
      })
    }
    let invalidCisIds = ''
    cisIdsList.forEach((cisId) => {
      if (!isAlphaNumericCisId(getIdElement(cisId))) {
        invalidCisIds += `${cisId}, `
      }
    })
    if (invalidCisIds) {
      return t('invalidCisIds', 'Invalid: {cisIds}', {
        cisIds: invalidCisIds.slice(0, -2),
      })
    }
    return null
  }
  const errorMessage = getErrorMessage()

  return (
    <>
      <Button emphasis="medium" onClick={overlay.handleClick}>
        {t('action.create', 'Create')}
      </Button>
      <Separator size="nano" />
      <DialogOverlay focusRef={focusElementRef} title={t('createPersonCopies', 'Create Person Copies')} {...overlay}>
        <Paragraph size="sm">
          {t('createPersonCopy.description', 'This will create a copy of this person into a patron’s private space')}
        </Paragraph>
        <TextArea
          ref={focusElementRef}
          id="cisIds"
          label={t('cisIds', 'CIS ID’s')}
          value={cisIds}
          onChange={(e) => setCisIds(e.target.value)}
          validMessage={getValidMessage()}
          errorMessage={errorMessage}
          disableResize
        />
        <DialogOverlayActions>
          <Button onClick={submitOnClick} emphasis="high" disabled={!cisIds || errorMessage}>
            {t('action.submit', 'Submit')}
          </Button>
          <Button onClick={overlay.close}>{t('action.cancel', 'Cancel')}</Button>
        </DialogOverlayActions>
      </DialogOverlay>
    </>
  )
}
